import 'keen-slider/keen-slider.min.css'

import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Arrow } from 'app/components/Common/Arrow'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { LabelCounter } from 'app/components/Common/LabelCounter'
import { styles, theme } from 'app/theme'
import { easeOutQuint, positionStyle } from 'app/utils/customFunctions'
import { useVocabularyData } from 'app/utils/vocabulary'
import { rgba } from 'emotion-rgba'
import { useKeenSlider } from 'keen-slider/react'
import { uniq } from 'lodash'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Parallax } from 'react-scroll-parallax'

import { FlexBox } from '../Layout/FlexBox'

const AUTOPLAY_TIMER = 5000

export interface Props {
  counter?: string
  cta?: ButtonProps
  label?: string
  images?: ImageProps[]
  subtitle?: string
  title?: string
  languageCode?: string
  variant?: Variant
}

export const VisualGallery = memo(function VisualGallery({
  counter,
  cta,
  label,
  images,
  subtitle,
  title,
  languageCode,
  variant = 'default',
}: Props) {
  if (!images) {
    return null
  }

  const [currentSlide, setCurrentSlide] = useState(0)
  const [details, setDetails] = useState<any>(null)
  const [loaded, setLoaded] = useState<boolean[]>([])
  const [nextLoaded, setNextLoaded] = useState<boolean[]>([])
  const timer = useRef<any>()

  const [sliderRef, instanceRef] = useKeenSlider({
    loop: true,
    drag: false,
    defaultAnimation: {
      duration: 1800,
      easing: easeOutQuint,
    },
    detailsChanged(s) {
      images.length > 1 ? setDetails(s.track.details) : null
      setCurrentSlide(s.track.details.rel)
    },
    breakpoints: {
      '(max-width: 1199px)': {
        drag: true,
      },
    },
  })

  useEffect(() => {
    const newLoaded = [...loaded]
    newLoaded[currentSlide] = true
    const newNextLoaded = [...nextLoaded]
    newNextLoaded[currentSlide + 1] = true

    setLoaded(newLoaded)
    setNextLoaded(newNextLoaded)

    document.addEventListener('keydown', function (e) {
      switch (e.keyCode) {
        case 37:
          instanceRef.current?.prev()

          break
        case 39:
          instanceRef.current?.next()

          break
      }
    })

    timer.current = setInterval(() => {
      if (instanceRef) {
        instanceRef.current?.next()
      }
    }, AUTOPLAY_TIMER)

    return () => {
      clearInterval(timer.current)
    }
  }, [currentSlide, instanceRef, sliderRef])

  const [animObjectRef, isAnimObjectVisible] = useInView({ triggerOnce: true })

  return (
    <Container variant={variant}>
      <Wrapper>
        {label ? (
          <FadeInUp delay={0.4}>
            <LabelCounter
              variant={variant === 'default' ? 'light' : 'dark'}
              dial={5}
              label={label}
              counter={counter}
            />
          </FadeInUp>
        ) : null}

        {variant == 'gallery' && languageCode ? (
          <FadeInUp delay={0.4}>
            <LabelCounter
              dial={5}
              label={useVocabularyData('gallery', languageCode)}
              counter="04"
            />
          </FadeInUp>
        ) : null}

        <Slider ref={sliderRef}>
          {uniq(images).map((item, index) => (
            <Slide key={index} className="keen-slider__slide">
              <Inner style={positionStyle(instanceRef, details, index)}>
                <Image {...item} />
              </Inner>
            </Slide>
          ))}

          <Text>
            <Parallax translateY={['0%', '-40%']}>
              {title ? <Title length={30} text={title} /> : null}

              {subtitle ? (
                <FadeInUp delay={0.4}>
                  <Subtitle>{subtitle}</Subtitle>
                </FadeInUp>
              ) : null}
              {cta ? (
                <FadeInUp delay={0.6}>
                  <CTA variant="hover-img" {...cta} />
                </FadeInUp>
              ) : null}
            </Parallax>
          </Text>
          {images.length > 1 ? (
            <CounterWrapper
              ref={animObjectRef}
              className={`${isAnimObjectVisible ? ' visible' : ''}`}
              dial={2}
              row
            >
              {uniq(images).map((item: any, index: number) => (
                <Counter
                  key={index}
                  className={`${currentSlide === index ? 'active' : ''}`}
                  onClick={() => {
                    instanceRef.current?.moveToIdx(index)
                  }}
                >
                  {`0${index + 1}`}
                </Counter>
              ))}
            </CounterWrapper>
          ) : null}

          {images.length > 1 && variant == 'gallery' ? (
            <Arrows>
              <Arrow
                onClick={(e) =>
                  e.stopPropagation() || instanceRef.current?.prev()
                }
              />

              <Arrow
                direction="R"
                onClick={(e) =>
                  e.stopPropagation() || instanceRef.current?.next()
                }
              />
            </Arrows>
          ) : null}
        </Slider>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section<ContainerProps>`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  margin-top: 9rem;
  text-align: center;

  ${({ theme, variant }) => {
    switch (variant) {
      case 'gallery':
        return css`
          background: ${theme.colors.variants.neutralLight1};
          margin-bottom: 9rem;

          @media (max-width: 1199px) {
            margin-bottom: 0;
          }
        `
      case 'light':
        return css`
          background: ${theme.colors.variants.neutralLight1};
          margin-bottom: 9rem;

          @media (max-width: 1199px) {
            margin-bottom: 0;
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    position: relative;
    margin-top: 0;
    padding: 2.25rem 1.5rem 1.5rem;
  }
`

const Text = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 2.25rem;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 46.875rem;
  margin: auto;
  padding-bottom: 2.25rem;

  @media (max-width: 1199px) {
    max-width: none;
    padding: 0 1.5rem;
    top: 50%;
    left: 50%;
    bottom: auto;
    transform: translate(-50%, -50%);

    > div {
      transform: none !important;
    }
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.xl}

  color: ${({ theme }) => theme.colors.variants.neutralLight3};
`

const Subtitle = styled.div`
  ${styles.description.light}

  font-size: 1rem;
  line-height: 1.25rem;
  margin-top: 0.5625rem;
  margin-bottom: 2.25rem;

  @media (max-width: 1199px) {
    display: none;
  }
`

const Wrapper = styled.div`
  padding: 4.5rem 5vw 5.25rem;
  position: relative;

  @media (max-width: 1199px) {
    margin-top: 0;
    padding: 0;
  }
`

const Slider = styled.div`
  display: flex;
  width: 100%;
  height: 80vh;
  max-height: 50rem;
  outline: none;
  overflow: hidden;
  position: relative;

  &:before {
    content: '';
    width: 100%;
    height: 60%;
    background: linear-gradient(
      ${rgba(theme.colors.variants.neutralDark3, 0)},
      ${rgba(theme.colors.variants.neutralDark3, 0.7)}
    );
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 2;
  }

  @media (max-width: 1439px) {
    max-height: 45rem;
  }

  @media (max-width: 1199px) {
    height: 52vh;
    max-height: 31.25rem;
    max-height: none;

    &:before {
      background: ${rgba(theme.colors.variants.neutralDark3, 0.4)};
      height: 100%;
    }
  }
`

const Slide = styled.div`
  overflow: hidden;
  position: relative;

  img {
    ${styles.img}

    z-index: 3;
  }
`

const Inner = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const CounterWrapper = styled(FlexBox)`
  position: absolute;
  bottom: 2.25rem;
  left: 50%;
  z-index: 2;
  opacity: 0;
  transform: translate(-50%, 2rem);
  transition: 0.4s 0.8s ease-in-out;

  &.visible {
    opacity: 1;
    transform: translate(-50%, 0);
  }

  @media (max-width: 1199px) {
    bottom: 1.5rem;
  }
`

const Counter = styled.div`
  ${styles.label}

  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1rem;
  opacity: 0.3;
  transition: opacity 0.4s ease-in-out;

  &:not(:last-of-type) {
    margin-right: 1.5rem;
  }

  &:hover,
  &.active {
    opacity: 1;
  }
`

const Arrows = styled.div`
  > div {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;

    &:first-of-type {
      left: 2.25rem;
    }
    &:last-of-type {
      right: 2.25rem;
    }
  }

  @media (max-width: 1199px) {
    > div {
      &:first-of-type {
        left: 1.5rem;
      }
      &:last-of-type {
        right: 1.5rem;
      }
    }
  }
`

const CTA = styled(Button)`
  &:hover {
    color: ${({ theme }) => theme.colors.variants.neutralLight3};
  }

  @media (max-width: 1199px) {
    margin-top: 0.75rem;
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'gallery' | 'light'
